"use client";

import Link from "next/link";
import { ShoppingCart, LayoutDashboard, LogIn, MapPin, ChevronDown, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSession } from "@/lib/auth/auth-client";
import { useCartStore } from "@/lib/store/cartStore";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useEffect, useState, useRef } from "react";

interface ShopHeaderProps {
  brandName?: string;
  brandId?: string;
  stores?: any[];
  selectedStoreId?: string;
  onStoreChange?: (storeId: string) => void;
}

export default function ShopHeader({ brandName, brandId, stores = [], selectedStoreId, onStoreChange }: ShopHeaderProps) {
  const { data: session, isPending } = useSession();
  const isLoggedIn = !!session?.user;
  const authResolved = !isPending;
  const { openCart, sessionId, initializeSession } = useCartStore();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const hasMultipleStores = stores.length > 1;
  const selectedStore = stores.find(s => s._id === selectedStoreId) || (stores.length === 1 ? stores[0] : null);
  const displayName = selectedStore?.name || brandName || "SHOP";

  // Initialize session for guest users
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Get cart item count from Convex
  // Skip until auth is resolved so we don't subscribe with wrong args
  // @ts-ignore - Type instantiation is excessively deep
  const cartItemCount = useQuery(
    api.cart.core.getCartItemCount,
    !authResolved
      ? "skip"
      : isLoggedIn
      ? {}
      : { sessionId: sessionId || undefined }
  );

  // Wishlist count — only for logged-in users
  // @ts-ignore
  const wishlistCount = useQuery(
    api.customers.customerDashboard.getWishlistCount,
    isLoggedIn && authResolved ? {} : "skip",
  ) ?? 0;

  const totalItems = cartItemCount ?? 0;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full bg-black text-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Left Side - Auth */}
          <div className="flex items-center shrink-0">
            {isPending ? (
              <div className="h-9 w-9 animate-pulse rounded-full bg-white/20" />
            ) : isLoggedIn ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:text-gray-200 hover:bg-white/10 gap-2"
                asChild
              >
                <Link href="/pos">
                  <LayoutDashboard className="h-4 w-4" />
                  <span className="hidden sm:inline">Access POS</span>
                </Link>
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:text-gray-200 hover:bg-white/10 gap-2"
                asChild
              >
                <Link href="/signin">
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </Link>
              </Button>
            )}
          </div>

          {/* Center - Store Selector or Brand Name */}
          <div className="flex-1 flex justify-center min-w-0 px-4" ref={dropdownRef}>
            {hasMultipleStores ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors max-w-[200px] sm:max-w-[280px]"
                >
                  <MapPin className="h-4 w-4 shrink-0 text-white/70" />
                  <span className="text-sm sm:text-base font-medium truncate">
                    {selectedStore ? displayName : "Select Branch"}
                  </span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-white/70 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                    {/* All Locations option */}
                    <button
                      onClick={() => {
                        onStoreChange?.("");
                        setDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        !selectedStoreId ? "bg-gray-50" : ""
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                        <MapPin className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">All Branches</p>
                        <p className="text-xs text-gray-500">Browse all branches</p>
                      </div>
                      {!selectedStoreId && (
                        <div className="ml-auto w-2 h-2 rounded-full bg-black shrink-0" />
                      )}
                    </button>

                    {/* Individual stores */}
                    {stores.map((store) => (
                      <button
                        key={store._id}
                        onClick={() => {
                          onStoreChange?.(store._id);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                          selectedStoreId === store._id ? "bg-gray-50" : ""
                        }`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-black/5 flex items-center justify-center shrink-0">
                          <MapPin className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{store.name}</p>
                          {store.address?.city && (
                            <p className="text-xs text-gray-500 truncate">{store.address.city}</p>
                          )}
                        </div>
                        {selectedStoreId === store._id && (
                          <div className="ml-auto w-2 h-2 rounded-full bg-black shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Single store — show brand name */
              <Link
                href="/"
                className="text-lg sm:text-xl font-light tracking-[0.15em] text-white hover:text-gray-200 transition-colors truncate max-w-[200px] sm:max-w-[300px]"
                title={displayName}
              >
                {displayName}
              </Link>
            )}
          </div>

          {/* Right Side - Wishlist + Cart */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Wishlist heart */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-gray-200 relative"
              asChild
            >
              <Link href="/account/favorites">
                <Heart
                  className={cn(
                    "h-5 w-5 transition-colors",
                    wishlistCount > 0 ? "fill-red-400 text-red-400" : "text-white"
                  )}
                />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {wishlistCount > 9 ? "9+" : wishlistCount}
                  </span>
                )}
              </Link>
            </Button>

            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-gray-200 relative"
              onClick={openCart}
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white text-black text-xs font-bold flex items-center justify-center">
                  {totalItems > 99 ? "99+" : totalItems}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
